import RadarChart from './RadarChart';
import ContentRomResult from './ContentRomTableResult';
import ContentRomResultSides from './ContentROMTableResultSides';
import { Flex, Row, Col, Typography, Spin, Tooltip, Button } from 'antd';
import './style.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTypedDispatch, useTypedSelector } from '@stores/index';
import { useEffect, useState } from 'react';
import { router } from '@routers/routers';
import { setActiveTab, setStateId } from '@stores/aiAssistant/aiAssistant';
import { getUserById } from '@stores/contacts/contacts';
import { ArrowLeft } from '@carespace-icons/arrows/arrowLeft';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { SummaryResultReportDownloader } from '@pages/AiAssistantCustomRomSummary/SummaryResultReportDownloader';
import { ACTIVETAB, getMobilityData, PLANS } from '@stores/constants';
import { getRomActivityStreamById } from '@stores/activityStream/activityStream';
import { ICustomRomSession } from '@stores/interfaces';
import MobilityPersonas from './MobilityPersonna';
import MobilityOverview from './MobilityOverview';
import { evalScore } from './jointsTemplateData';
import PieChart from './PieChart';
import OldRadarChart from './OldRadarChart';
import { FilterLines } from '@carespace-icons/general/filterLines';

const { Text } = Typography;

interface ICScreen {
	isDashboard?: boolean;
}

const AiAssistantCustomRomScanResult = (props: ICScreen) => {
	const { isDashboard } = props;
	const [selectedButton, setSelectedButton] = useState<string>('pie');

	const { state } = useLocation();
	const { t } = useTranslation();
	const [avgScore, setAvgScore] = useState(0);
	const mobilityOptions = getMobilityData(Math.round(avgScore), t);
	const persona = mobilityOptions.find(option => option.active);
	const navigate = useNavigate();
	const dispatch = useTypedDispatch();
	const { selectedRom } = useTypedSelector(state => state.rom.customRom);
	const user = useTypedSelector(state => state.user);
	const { selectedUser } = useTypedSelector(state => state.contacts.main);
	const userId = user.isPhysioterapist ? selectedUser?.id : user?.id;
	const userPlans = useTypedSelector(
		state => state.settings.settings.savedUserPlans,
	);
	const isPlanOne = userPlans?.planType === PLANS?.SCREENING;
	const [isLoading, setLoading] = useState(true);
	const [sessionData, setSessionData] = useState<ICustomRomSession | null>(
		{} as ICustomRomSession,
	);

	const getColorForCategory = (score: number) => {
		if (Math.round(score) >= 90) return '#52c41a';
		if (Math.round(score) >= 75 && Math.round(score) <= 89) return '#73D13D';
		if (Math.round(score) >= 60 && Math.round(score) <= 74) return '#faad14';
		if (Math.round(score) >= 40 && Math.round(score) <= 59) return '#F59712';
		if (Math.round(score) >= 20 && Math.round(score) <= 39) return '#F2615B';
		return '#d9363e';
	};

	const getCategoryName = (score: number) => {
		if (score >= 90) return t('Patient.data.omnirom.optimalMobility');
		if (score >= 75) return t('Patient.data.omnirom.functionalMobility');
		if (score >= 60) return t('Patient.data.omnirom.moderateRestriction');
		if (score >= 40) return t('Patient.data.omnirom.restrictedMobility');
		if (score >= 20) return t('Patient.data.omnirom.severelyRestricted');
		return t('Patient.data.omnirom.criticallyImpaired');
	};

	useEffect(() => {
		if (sessionData?.romPatientResults) {
			const totalScore = sessionData?.romPatientResults?.reduce((sum, item) => {
				const itemScore = item?.results?.reduce((innerSum, result) => {
					const score = Math.round(evalScore(result));
					return innerSum + score;
				}, 0);
				return sum + (itemScore || 0);
			}, 0);
			const totalResultsLength = sessionData?.romPatientResults?.reduce(
				(total, { results }) => total + (results?.length || 0),
				0,
			);

			setAvgScore(Math.round(totalScore / totalResultsLength));
		}
	}, [sessionData]);

	const getCategoryDesc = (score: number) => {
		if (score >= 90) return t('Patient.data.omnirom.optimalMobilityDesc');
		if (score >= 75) return t('Patient.data.omnirom.functionalMobilityDesc');
		if (score >= 60) return t('Patient.data.omnirom.moderateRestrictionDesc');
		if (score >= 40) return t('Patient.data.omnirom.limitedMobilityDesc');
		if (score >= 20) return t('Patient.data.omnirom.restrictedMobilityDesc');
		return t('Patient.data.omnirom.criticallyImpairedDesc');
	};

	useEffect(() => {
		const pathParts = window.location.pathname.split('/');
		const romIndex = pathParts.indexOf('rom');
		if (romIndex !== -1 && pathParts.length > romIndex + 1) {
			const romId = pathParts[romIndex + 1];
			if (!selectedRom?.id && romId) {
				dispatch(setActiveTab(ACTIVETAB.ROM_SCAN_RESULTS));
				fetchData(romId);
			}
		}
		if (selectedRom?.id) {
			fetchData(selectedRom.id);
		}
	}, [selectedRom, user, selectedUser]);

	const fetchData = async (romId: string) => {
		const sessionData = await dispatch(getRomActivityStreamById({ romId }));
		setSessionData(sessionData.payload as ICustomRomSession);
		setLoading(false);
	};

	const handleEmptySelectedRom = async () => {
		navigate(`/${userId}${router.AIASSISTANT_ROM_SUMMARY}`);
		dispatch(setActiveTab('romSummary'));
		await dispatch(getUserById(userId));
	};

	const handleArrowClick = async () => {
		navigate(`/${sessionData?.userId}${router.AIASSISTANT_ROM_SUMMARY}`, {
			state: {
				sessionId: sessionData?.romProgramId,
			},
		});
		dispatch(setActiveTab('romSummary'));
		dispatch(setStateId(sessionData?.id));
	};

	{
		if (isLoading) {
			return <Spin className="spin-css" tip="Loading" size="large" />;
		}
	}

	const options = [
		{
			label: '',
			value: 'filter',
			icon: <FilterLines color="stroke-black" width={20} height={18} />,
		},
		{ label: t('Pie Chart'), value: 'pie' },
		{ label: t('Radar Chart'), value: 'radar' },
	];

	return (
		<Flex
			vertical
			style={{
				padding: '5px 25px 25px',
				backgroundColor: 'var(--summary-bg-colors)',
			}}>
			<div
				style={{
					display: 'flex',
					alignItems: 'end',
					justifyContent: 'space-between',
					gap: 10,
					marginBottom: '10px',
				}}>
				<div
					style={{
						display: 'flex',
						alignItems: 'end',
						gap: 10,
						color: 'var(--text-color-root)',
						fontWeight: 'bold',
						fontSize: '20px',
					}}>
					{location.pathname != '/' &&
						!location.pathname.includes(router.PATIENTVIEW) && (
							<span onClick={handleArrowClick} style={{ cursor: 'pointer' }}>
								<ArrowLeft color="stroke-[--text-color-root]" />
							</span>
						)}
					<p>{t('Patient.data.omnirom.romMobilityReport')}</p>
					<p>- {sessionData?.romProgram?.title}</p>
					{!state?.status ? (
						<>
							<Tooltip
								title={t(
									'Admin.data.menu.aiAssistant.aiAssistantRomSummary.download',
								)}>
								<span>
									<SummaryResultReportDownloader
									chartType={selectedButton}
										selectedRom={sessionData || null}
										user={user.isPhysioterapist ? selectedUser : user}
										reportType="edit"
										completedStatus={state?.status}
									/>
								</span>
							</Tooltip>
						</>
					) : (
						<>
							{!isDashboard && (
								<>
									<Tooltip
										title={t(
											'Admin.data.menu.aiAssistant.aiAssistantRomSummary.edit',
										)}>
										<span>
											<SummaryResultReportDownloader
												selectedRom={sessionData || null}
									chartType={selectedButton}

												user={user.isPhysioterapist ? selectedUser : user}
												reportType="edit"
												completedStatus={state?.status}
											/>
										</span>
									</Tooltip>
									<Tooltip
										title={t(
											'Admin.data.menu.aiAssistant.aiAssistantRomSummary.qrCode',
										)}>
										<span>
											<SummaryResultReportDownloader
												selectedRom={sessionData || ({} as ICustomRomSession)}
												user={user.isPhysioterapist ? selectedUser : user}
												reportType="qr"
									chartType={selectedButton}

												completedStatus={state?.status}
											/>
										</span>
									</Tooltip>
									<Tooltip
										title={t(
											'Admin.data.menu.aiAssistant.aiAssistantRomSummary.sendEmail',
										)}>
										<span>
											<SummaryResultReportDownloader
												selectedRom={sessionData || ({} as ICustomRomSession)}
												user={user.isPhysioterapist ? selectedUser : user}
												reportType="email"
									chartType={selectedButton}

												completedStatus={state?.status}
											/>
										</span>
									</Tooltip>
								</>
							)}
							<Tooltip
								title={t(
									'Admin.data.menu.aiAssistant.aiAssistantRomSummary.download',
								)}>
								<span>
									<SummaryResultReportDownloader
										selectedRom={sessionData || ({} as ICustomRomSession)}
										user={user.isPhysioterapist ? selectedUser : user}
										reportType="download"
									chartType={selectedButton}

										completedStatus={state?.status}
									/>
								</span>
							</Tooltip>
						</>
					)}
				</div>
				<Text
					type="secondary"
					style={{
						color: 'var(--text-color-root)',
					}}>
					{moment(selectedRom?.createdAt).local().format('LLL')}
				</Text>
			</div>
			<div style={{ marginLeft: '10px', marginRight: '10px' }}>
				<Row gutter={[24, 16]}>
					<Col
						span={12}
						style={{
							padding: '10px',
							backgroundColor: 'white',
							borderRadius: '14px',
						}}>
						<div
							className="mobility-outer-container"
							style={{ backgroundColor: '#f2f4f7' }}>
							<div className="mobility-icon">
								<img src={persona?.selectedIcon} alt={persona?.title} />
							</div>
							<div className="mobility-text">
								<div
									className="mobility-title-text"
									style={{ color: persona?.color }}>
									{t('Patient.data.mobilityReport.youAre')} "
									{persona?.title?.toUpperCase()}"
								</div>
								<div className="mobility-description">{persona?.range}</div>
							</div>
						</div>
						<Button.Group
							style={{
								width: '100%',
								justifyContent: 'end',
								padding: '10px',
							}}>
							{options.map(item => {
								const isSelected = selectedButton === item.value;
								return (
									<Button
										key={item.label}
										icon={item.icon}
										style={{
											borderColor: 'inherit',
											borderRadius: '0px',
											backgroundColor: isSelected ? '#D0D5DD' : 'white',
											color: '#1D2939',
										}}
										onClick={() => {
											if (item.value != 'filter') {
												setSelectedButton(item.value);
											}
										}}>
										{item.label}
									</Button>
								);
							})}
						</Button.Group>
						{selectedButton === 'radar' ? (
							<OldRadarChart
								selectedRom={sessionData}
								persona={persona!}
								avgScore={avgScore}
							/>
						) : (
							<PieChart
								selectedRom={sessionData}
								persona={persona!}
								avgScore={avgScore}
							/>
						)}
					</Col>
					<Col span={12}>
						<ContentRomResultSides
							selectedRom={sessionData || ({} as ICustomRomSession)}
							getColorForCategory={getColorForCategory}
							getCategoryName={getCategoryName}
							getCategoryDesc={getCategoryDesc}
						/>
					</Col>
				</Row>
			</div>
			<MobilityOverview persona={persona!} isPdf={false} />
			<ContentRomResult
				selectedRom={sessionData || ({} as ICustomRomSession)}
				getColorForCategory={getColorForCategory}
				getCategoryName={getCategoryName}
			/>
			<MobilityPersonas avgScore={avgScore} isPdf={false} />
		</Flex>
	);
};
export default AiAssistantCustomRomScanResult;
